import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import { Loading } from '@monorepo/shared/componentsV2/Loading';
import { TextBlock } from '@monorepo/shared/componentsV2/TextBlock';
import { Tooltip } from '@monorepo/shared/componentsV2/Tooltip';
import { VisuallyHidden } from '@monorepo/shared/componentsV2/VisuallyHidden';
import { useCurrentProject } from '@monorepo/shared/hooks/useCurrentProject';
import { useProjectUsersById } from '@monorepo/shared/hooks/useProjectUsersById';
import { bodySmall } from '@monorepo/shared/styles/text';
import RecurringIcon from '@svg/recurring.svg';
import { format } from 'date-fns';
import {
  ComplianceCalendarEvent,
  ComplianceCalendarEventStatus,
  ComplianceCalendarEventType,
  localEquivalentOfUTC,
} from 'mapistry-shared';
import React, { RefObject, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { EventStatusIndicator } from '../../EventStatusIndicator';
import { CalendarEvent } from '../CalendarEvent';
import { CalendarEventTooltip } from '../CalendarEventTooltip';
import { useComplianceCalendarContext } from '../ComplianceCalendarContext';
import {
  areAllDayEventsCompleted,
  areAllDayEventsExceptions,
  getDaySummaryText,
  getDaysWithEvents,
  getEventLinkProps,
  groupEventsByDay,
  isPastUtcDay,
  isTodayUtcDay,
} from '../helpers/complianceCalendarHelpers';
import {
  CALENDAR_EVENT_SECTIONS,
  getEventsForSection,
  getSectionEmptyMessage,
} from '../helpers/complianceCalendarWeekViewHelpers';
import {
  EmptySectionParagraph,
  ListDayHeaderDiv,
  ListDaySection,
  ListDaySummaryDiv,
  ListDayTitle,
  ListViewCard,
  ListViewEventLink,
  ListViewSectionAccordion,
  StyledCalendarEvent,
  TodayChip,
} from '../styled';

const StyledAssigneeSubtextDiv = styled.div`
  /* sit under the inspection title, not the status icon */
  padding-left: calc(1rem + 5px);
  overflow: hidden;
  ${bodySmall}
  color: ${({ theme }) => theme.colors.gray666};
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ListEventTitleRow = styled.div`
  display: flex;

  ${StyledCalendarEvent} {
    flex: 1;
    min-width: 0;
  }
`;

const RecurringIconWrap = styled.span`
  padding-right: 0.5rem;
  color: ${({ theme }) => theme.colors.gray666};

  & svg {
    width: 0.875rem;
    height: 0.875rem;
    fill: currentcolor;
  }
`;

type ComplianceCalendarListViewProps = {
  events: ComplianceCalendarEvent[];
  exclusiveEnd: Date;
  isLoading: boolean;
  navigateToEventUrl: (url: string) => void;
  rangeStart: Date;
  scrollContainerRef: RefObject<HTMLDivElement>;
  setTodaySectionRef: (element: HTMLElement | null) => void;
  summaryEvents: ComplianceCalendarEvent[];
};

export function ComplianceCalendarListView({
  events,
  exclusiveEnd,
  isLoading,
  navigateToEventUrl,
  rangeStart,
  scrollContainerRef,
  setTodaySectionRef,
  summaryEvents,
}: ComplianceCalendarListViewProps) {
  const { filters } = useComplianceCalendarContext();
  const visibleSections = CALENDAR_EVENT_SECTIONS.filter((section) =>
    filters.eventTypes.includes(section.type),
  );

  const { project } = useCurrentProject();
  const { usersById } = useProjectUsersById({
    organizationId: project?.organization_id,
    projectId: project?.id,
  });

  const daysWithEvents = useMemo(
    () =>
      getDaysWithEvents({
        events,
        exclusiveEnd,
        rangeStart,
      }).filter(({ events: dayEvents }) => dayEvents.length > 0),
    [events, exclusiveEnd, rangeStart],
  );

  const summaryEventsByDay = useMemo(
    () => groupEventsByDay(summaryEvents),
    [summaryEvents],
  );

  // scroll to top if the start date changes
  useEffect(() => {
    scrollContainerRef.current?.scrollTo({ top: 0 });
  }, [rangeStart, scrollContainerRef]);

  if (isLoading) {
    return (
      <ListViewCard variant="outlined">
        <Loading />
      </ListViewCard>
    );
  }

  const getAssigneeSubtext = (event: ComplianceCalendarEvent) => {
    let subtext: string | undefined;
    if (
      usersById &&
      (event.type === ComplianceCalendarEventType.INSPECTION ||
        event.type === ComplianceCalendarEventType.TASK)
    ) {
      subtext = event.assigneeIds
        ?.map((id) => usersById[id]?.name || 'Unknown user')
        .join(', ');
    }

    if (subtext) {
      return (
        <StyledAssigneeSubtextDiv>
          Assigned to: {subtext}
        </StyledAssigneeSubtextDiv>
      );
    }

    return undefined;
  };

  const getIsRecurringIcon = (event: ComplianceCalendarEvent) => {
    const isRecurringTask =
      event.type === ComplianceCalendarEventType.TASK && event.isRecurring;

    if (isRecurringTask) {
      return (
        <Tooltip title="Recurring task">
          <RecurringIconWrap>
            <RecurringIcon aria-hidden />
            <VisuallyHidden>Recurring task</VisuallyHidden>
          </RecurringIconWrap>
        </Tooltip>
      );
    }

    return undefined;
  };

  const getDaySummaryIcon = (daySummaryEvents: ComplianceCalendarEvent[]) => {
    const isDayOnlyExceptions = areAllDayEventsExceptions(daySummaryEvents);
    const isDayFullyCompleted = areAllDayEventsCompleted(daySummaryEvents);

    if (isDayOnlyExceptions) {
      return (
        <EventStatusIndicator
          status={ComplianceCalendarEventStatus.EXCEPTION}
        />
      );
    }

    if (isDayFullyCompleted) {
      return (
        <EventStatusIndicator status={ComplianceCalendarEventStatus.COMPLETE} />
      );
    }

    return undefined;
  };

  return (
    <ListViewCard ref={scrollContainerRef} variant="outlined">
      {daysWithEvents.length === 0 ? (
        <TextBlock primaryText="No events" />
      ) : (
        daysWithEvents.map(({ day, dayKey, events: dayEvents }) => {
          const dayLabel = format(
            localEquivalentOfUTC(day),
            'EEEE, MMMM d, yyyy',
          );
          const isPast = isPastUtcDay(day);
          const daySummaryEvents = summaryEventsByDay.get(dayKey) ?? dayEvents;
          const isDayFullyCompleted =
            areAllDayEventsCompleted(daySummaryEvents);
          const shouldCollapseDay = isPast && isDayFullyCompleted;

          return (
            <ListDaySection
              key={dayKey}
              ref={isTodayUtcDay(day) ? setTodaySectionRef : undefined}
              aria-label={dayLabel}
              $isPast={isPast}
            >
              <ListDayHeaderDiv>
                <ListDayTitle>
                  {dayLabel}
                  {isTodayUtcDay(day) && <TodayChip label="Today" />}
                </ListDayTitle>
                <ListDaySummaryDiv>
                  {getDaySummaryIcon(daySummaryEvents)}
                  {getDaySummaryText(daySummaryEvents)}
                </ListDaySummaryDiv>
              </ListDayHeaderDiv>

              {visibleSections.map((section) => {
                const sectionEvents = getEventsForSection(
                  dayEvents,
                  section.type,
                );

                return (
                  <ListViewSectionAccordion
                    key={section.type}
                    defaultExpanded={!shouldCollapseDay}
                    variant="condensed"
                    color="secondary"
                    expandIcon={<ArrowDropUpIcon />}
                    summaryContent={section.label}
                    detailsContent={
                      sectionEvents.length ? (
                        sectionEvents.map((event) => (
                          <CalendarEventTooltip key={event.id} event={event}>
                            {(titleRef) => (
                              <ListViewEventLink
                                {...getEventLinkProps(
                                  event,
                                  navigateToEventUrl,
                                )}
                              >
                                <ListEventTitleRow>
                                  <CalendarEvent
                                    status={event.status}
                                    title={event.title}
                                    titleRef={titleRef}
                                    truncation="horizontal"
                                  />
                                  {getIsRecurringIcon(event)}
                                </ListEventTitleRow>
                                {getAssigneeSubtext(event)}
                              </ListViewEventLink>
                            )}
                          </CalendarEventTooltip>
                        ))
                      ) : (
                        <EmptySectionParagraph>
                          {getSectionEmptyMessage({
                            section,
                            daySummaryEvents,
                          })}
                        </EmptySectionParagraph>
                      )
                    }
                  />
                );
              })}
            </ListDaySection>
          );
        })
      )}
    </ListViewCard>
  );
}
